import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Loader, useExternalScript } from "@certn/cascadia-react";
import { APIContext } from "@certn/api";
import { getRegionKey } from "@certn/utils";
import { useTranslation } from "react-i18next";

import { AdaChatContext } from "./AdaChatContext";
import { useAuth } from "./hooks/useAuth";
import "./ChatBotWrapper.css";

declare global {
  interface Window {
    adaEmbed?: {
      setSensitiveMetaFields: (
        fields: Record<string, unknown>
      ) => Promise<void>;
      toggle: () => Promise<void>;
    };
    adaSettings: {
      handle: string;
      cluster: string;
      sensitiveMetaFields?: { jwt_token: string };
      metaFields?: Record<string, unknown>;
      zdChatterAuthCallback?: (callback: (token: string) => void) => void;
      align?: string;
      enableProgrammaticControl?: boolean;
      eventCallbacks?: Record<string, () => void>;
      toggleCallback?: (isDrawerOpen: boolean) => void;
      adaReadyCallback?: (params: { isRolledOut: boolean }) => void;
    };
  }
}

interface ChatBotWrapperProps extends PropsWithChildren {
  currentEnvironment: string;
  currentAppName: string;
  /**
   * Which side of the screen Ada's chat drawer opens on. Ada's launcher
   * bubble is hidden, but its `align` setting still positions the drawer.
   * Defaults to "right" so the drawer doesn't cover left-anchored navigation.
   */
  chatAlign?: "left" | "right";
}

export function ChatBotWrapper(props: ChatBotWrapperProps) {
  const { currentEnvironment, currentAppName, chatAlign = "right" } = props;
  const { i18n } = useTranslation();
  const userSelectedLang = i18n.language;

  const scriptStatus = useExternalScript(
    "https://static.ada.support/embed2.js"
  );
  const auth = useAuth();
  const { domain, latestToken } = useContext(APIContext);

  const [isReady, setIsReady] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const isOpenRef = useRef(false);

  const handleReady = useCallback(
    ({ isRolledOut }: { isRolledOut: boolean }) => setIsReady(isRolledOut),
    []
  );

  const handleMessageReceived = useCallback(() => {
    if (!isOpenRef.current) setHasUnread(true);
  }, []);

  const handleToggle = useCallback((isDrawerOpen: boolean) => {
    isOpenRef.current = isDrawerOpen;
    if (isDrawerOpen) setHasUnread(false);
  }, []);

  useEffect(() => {
    window.adaSettings = {
      handle: "certn",
      cluster: "maple",
      sensitiveMetaFields: {
        jwt_token: latestToken || "",
      },
      metaFields: {
        user_id: auth.user?.sub,
        user_email: auth.user?.email,
        user_name: auth.user?.name,
        app_name: currentAppName,
        region_centric: getRegionKey().toUpperCase(),
        current_environment: currentEnvironment,
        domain,
        language: userSelectedLang,
      },
      align: chatAlign,
      enableProgrammaticControl: true,
      eventCallbacks: {
        "ada:message:received": handleMessageReceived,
      },
      toggleCallback: handleToggle,
      adaReadyCallback: handleReady,
    };
  }, [
    latestToken,
    domain,
    currentEnvironment,
    userSelectedLang,
    auth.user,
    auth.user?.email,
    auth.user?.name,
    currentAppName,
    chatAlign,
    handleMessageReceived,
    handleToggle,
    handleReady,
  ]);

  useEffect(() => {
    const waitForAda = () => {
      if (window.adaEmbed && window.adaEmbed.setSensitiveMetaFields) {
        window.adaEmbed
          .setSensitiveMetaFields({
            jwt_token: latestToken,
          })
          .catch((err) =>
            console.error("Error setting sensitiveMetaFields:", err)
          );
      }
    };

    waitForAda();
  }, [auth.user, latestToken]);

  const toggle = useCallback(() => {
    window.adaEmbed
      ?.toggle()
      .catch((err) => console.error("Error toggling Ada chat:", err));
  }, []);

  const contextValue = useMemo(
    () => (isReady ? { hasUnread, toggle } : null),
    [isReady, hasUnread, toggle]
  );

  if (scriptStatus === "loading") {
    return <Loader overlay />;
  }

  return (
    <AdaChatContext.Provider value={contextValue}>
      {props.children}
    </AdaChatContext.Provider>
  );
}
